import React from 'react';
import { QuizCard1 } from './QuizCards/QuizCard1';
import { QuizCard2 } from './QuizCards/QuizCard2';
import { QuizCard3 } from './QuizCards/QuizCard3';
import { QuizCard4 } from './QuizCards/QuizCard4';
import { QuizResultsCard } from './QuizCards/QuizResults';
import { QuizCardInputs } from './QuizCards/QuizCardsInput';
import './Quiz.scss';

export interface QuizConfigItem {
    heading: string;
    cardTitle: string;
    valid: boolean;
    value: { [key: string]: string | number | boolean };
    text?: string;
    contents: React.FC<QuizCardInputs>;
    answers?: { [key: string]: string }[];
}

enum QuestionType {
    Size = `paragraph__question_size`,
    Goal = `paragraph__question_goal`,
    Zip = `paragraph__question_zip`,
    Industry = `paragraph__question_industry`,
    Thank_You = `paragraph__thank_you`,
}

/** Creates an answer array of type `{answerType: answerValue}[]` from the nested answers relationships returned in the GraphQL response
 * @param answerArr The array of answer objects returned from the GraphQL response
 * @returns A parsed array of answers in type `{answerType: answerValue}[]` to pass to quizConfig objects
 */
export const parseAnswers = (answerArr) => {
    if (!answerArr) return [];
    const answers: { [key: string]: string }[] = [];
    answerArr.forEach((answer) => {
        answers.push({
            [answer.relationships.field_answer_term.name.toLowerCase()]:
                answer.field_answer_copy as string,
        });
    });
    return answers;
};

/**
 * Gets the image data and alt text from the GraphQL response
 * @param qaData GraphQL data object for a question/answer
 * @returns An object containing the image data and alt text for the question/answer
 */
const parseImageData = (qaData) => {
    const imageData =
        qaData?.relationships?.field_image?.relationships?.field_media_image
            ?.localFile;
    const alt = qaData?.relationships?.field_image?.field_media_image?.alt;
    return {
        imageData,
        alt,
    };
};

export const createQuizConfig = (questionAnswerArr) => {
    let config: QuizConfigItem[] = [];
    const configItemBase: QuizConfigItem = {
        heading: ``,
        cardTitle: ``,
        text: ``,
        valid: false,
        value: {},
        contents: undefined,
        answers: [],
    };

    // Early return if there's no question/answer array
    if (!questionAnswerArr) return config;

    // Creates a config object for each question that can be consumbed by the QuizCard listed under the 'contents' property.
    config = questionAnswerArr.map((qa) => {
        const { type: qType } = qa.internal;
        const quizItem = { ...configItemBase };

        // Early return if there's no question or question type
        if (!qa.field_question || !qType) return;

        quizItem.heading = qa.field_question;

        let qAnswers = [];
        let imageData = undefined;
        let alt = undefined;

        switch (qType) {
            case QuestionType.Goal:
                qAnswers = parseAnswers(qa?.relationships?.field_answer_goals);
                quizItem.cardTitle = `Choose One:`;
                quizItem.contents = QuizCard2;
                quizItem.answers = qAnswers;
                return quizItem;
            case QuestionType.Size:
                qAnswers = parseAnswers(qa?.relationships?.field_answer);
                quizItem.cardTitle = `Choose One:`;
                quizItem.contents = QuizCard1;
                quizItem.answers = qAnswers;
                return quizItem;
            case QuestionType.Zip:
                quizItem.cardTitle = `Enter Your Zip Code:`;
                quizItem.contents = QuizCard3;
                return quizItem;
            case QuestionType.Industry:
                quizItem.cardTitle = `Select Your Industry:`;
                quizItem.contents = QuizCard4;
                return quizItem;
            case QuestionType.Thank_You:
                imageData = parseImageData(qa).imageData;
                alt = parseImageData(qa).alt;
                quizItem.text = qa?.field_body_copy ?? ``;
                quizItem.cardTitle = ``;
                quizItem.contents = QuizResultsCard.bind(null, {
                    imageData,
                    alt,
                });

                return quizItem;
            default:
                return null;
        }
    });

    return config;
};
